import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Rating from '@mui/material/Rating';
import { withStyles } from '@material-ui/core/styles';
import {
  IAllLandingBusiness,
  ILandingBusiness,
} from '../../interfaces/ILandingBusiness';
import { IPlayer } from '../../interfaces/IPlayer';
import { IClient } from '../../interfaces/IClient';
import { useLandingBusiness } from '../../hooks/useLandingBusiness';
import { Loading } from '../../components/Loading';
import { HelmetComponent } from '../../components/Helmet';
import FGTS from '../../assets/images/FGTS.png';
import podeEntrar from '../../assets/images/podeEntrar.png';
import casaVerdeEAmarela from '../../assets/images/casaVerdeEAmarela.png';
import minhaCasaMinhaVida from '../../assets/images/minhaCasaMinhaVida.png';
import { WhatsAppCapture } from '../Action/components/WhatsAppCapture';
import { api } from '../../server/api';
import { Notify } from '../../components/Notify';
import { maskMoney, maskMoneyWithText, maskTEL } from '../../utils/mask';
import { ImageModal } from './components/imageModal';
import { FeatureIcon } from './components/featureIcon';
import { ShareModal } from './components/shareModal';
import * as routes from '../../constants/Routes';
import { IFrameModal } from './components/iFrameModal';

const StyledRating = withStyles({
  iconFilled: {
    color: '#ffc107',
  },
})(Rating);

export function LandingBusiness() {
  const navigate = useNavigate();
  const { playerId, bussinessId } = useParams();
  const { getLandingBusinessPage, landingBusinessPage } = useLandingBusiness();
  const [player, setPlayer] = useState<IPlayer>();
  const [client, setClient] = useState<IClient>();
  const [landingBusiness, setLandignBusiness] = useState<ILandingBusiness>();
  const [clientLandingBusiness, setClientLandingBusiness] =
    useState<IAllLandingBusiness[]>();
  const [rating, setRating] = useState<number>();
  const [activeTab, setActiveTab] = useState<string>('apartment');
  const [loading, setLoading] = useState<boolean>(true);
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [isOpenWhatsAppCapture, setIsOpenWhatsAppCapture] = useState(false);
  const [leadName, setLeadName] = useState<string>();
  const [leadLastName, setLeadLastName] = useState<string>();
  const [leadPhone, setLeadPhone] = useState<string>();
  const [leadEmail, setLeadEmail] = useState<string>();
  const [like, setLike] = useState<boolean>(false);
  const [share, setShare] = useState<boolean>(false);
  const [shareModal, setShareModal] = useState<boolean>(false);
  const [blueprintModal, setBlueprintModal] = useState(false);
  const [iFrameModal, setIFrameModal] = useState(false);
  const [imageModal, setImageModal] = useState(false);
  const [loadingDelay, setLoadingDelay] = useState(true);

  const images = useMemo(() => {
    if (
      (landingBusiness?.medias?.images?.housingUnitImages &&
        activeTab === 'apartment') ||
      activeTab === 'neighborhood'
    )
      return landingBusiness.medias.images.housingUnitImages;
    if (
      landingBusiness?.medias?.images?.commonAreasImages &&
      activeTab === 'condo'
    )
      return landingBusiness.medias.images.commonAreasImages;
  }, [activeTab, landingBusiness]);

  useEffect(() => {
    getLandingBusinessPage(playerId, bussinessId);

    const timer = setTimeout(() => {
      setLoadingDelay(false);
    }, 3000);

    // Limpa o timeout se o componente for desmontado
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (landingBusinessPage) {
      setPlayer(landingBusinessPage.player);
      setClient(landingBusinessPage.client);
      setLandignBusiness(landingBusinessPage.landingBusiness);
      setClientLandingBusiness(landingBusinessPage.clientLandingBusinesses);
      setRating(landingBusinessPage.playerRating);
      setLoading(false);
    }
  }, [landingBusinessPage]);

  const saveLead = useCallback(
    async (lead: any) => {
      try {
        const leadData = {
          name: lead.leadName,
          lastname: lead.leadLastName,
          email: lead.leadEmail,
          phone: `+55 ${lead.leadPhone}`,
          businessId: landingBusiness._id,
          playerId: player.id,
          clientId: client.id,
          converted: false,
          leadToken:
            Math.random().toString(36).substr(2, 9) +
            Math.random().toString(36).substring(2, 9),
          acceptedTerms: 'true',
          businessName: '',
          liked: like,
          shared: share,
        };

        const parameters = {
          createFunctionality: 'registerLead',
          userType: 'consumer',
          lead: leadData,
          clientId: client.id,
          playerId: player.id,
          validationCode: '',
          urlOrigem: window.location.href,
        };

        const { data } = await api.post('/create', parameters);

        if (data.success) {
          setIsOpenWhatsAppCapture(false);
          if (data.message === 'Os dados já se encontram em nossa base.') {
            Notify(
              'Seus dados já estão na plataforma. Logo entraremos em contato!',
              'alert'
            );
          } else {
            Notify(
              'Seus dados foram enviados com sucesso! Entraremos em contato em breve.',
              'success'
            );
          }
        }
        if (data.message === 'Os dados já se encontram em nossa base.') {
          Notify(
            'Seus dados ja se encontram na nossa base de dados. Entraremos em contato em breve. ',
            'info'
          );
        } else if (!data.success) {
          throw Error;
        }
      } catch (error) {
        console.log('saveLead error: ', error);
        Notify(
          'Ocorreu um erro ao enviar seus dados. Por favor, tente novamente mais tarde.',
          'error'
        );
      }
    },
    [landingBusiness, player, client, like, share]
  );

  const sendLike = useCallback(async () => {
    try {
      const { data } = await api.patch(
        `/landing-business/likes/${landingBusiness._id}`
      );
      if (data) {
        setLandignBusiness({
          ...landingBusiness,
          stats: { ...landingBusiness.stats, likes: data.body.stats?.likes },
        });
      }
    } catch (error) {
      console.log('sendLike error: ', error);
    }
  }, [landingBusiness]);

  const sendShare = useCallback(async () => {
    try {
      const { data } = await api.patch(
        `/landing-business/shares/${landingBusiness._id}`
      );
      if (data) {
        setLandignBusiness({
          ...landingBusiness,
          stats: {
            ...landingBusiness.stats,
            shares: data.body.stats?.shares,
          },
        });
      }
    } catch (error) {
      console.log('sendShare error: ', error);
    }
  }, [landingBusiness]);

  return (
    <div>
      <HelmetComponent
        actionTitle={landingBusiness?.details?.name}
        clientLogoURL={client?.logoURL}
        companyName={client?.companyName}
        clientFavicon={client?.favIcon}
        primaryColor={client?.primaryColor}
        secondaryColor={client?.secondaryColor}
        tabTitle={landingBusiness?.details?.name}
      />
      {loading && <Loading />}
      {!loading && (
        <div
          style={{
            padding: '10px 35px',
            backgroundColor: 'white',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '25px',
            }}
          >
            <img
              src={client.logoURL}
              alt="Renda Mais Logo"
              style={{ height: '48px' }}
            />
          </div>

          {/* Agent Profile */}
          {landingBusiness?.details?.type !== 'Alto padrao' && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img
                src={player?.photoURL}
                alt="Profile"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
              <div
                style={{
                  display: 'flex',
                  marginTop: '20px',
                }}
              >
                <div
                  className="d-flex justify-content-center position-relative "
                  style={{
                    top: '-1 5px',
                    fontSize: '18px',
                    fontWeight: '500',
                    alignItems: 'center',
                    gap: '7px',
                  }}
                >
                  <StyledRating value={rating} precision={0.1} readOnly />{' '}
                  {rating}
                </div>
              </div>
              <h2
                style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  marginTop: '8px',
                  color: '#666',
                }}
              >
                {player.name}
              </h2>
              <p
                style={{
                  color: '#666',
                }}
              >
                tem uma indicação para você
              </p>
            </div>
          )}

          {/* Main Image */}
          {loadingDelay ? (
            <div style={{ marginLeft: '-35px' }}>
              <Loading />
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: `${client.primaryColor}`,
                  color: 'white',
                  padding: '7px 12px',
                  marginRight: 'auto',
                  marginLeft: 'auto',
                  marginTop: '10px',
                  width: window.innerWidth > 768 ? '95%' : '90%',
                  borderRadius: '10px',
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    width: '42%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {landingBusiness.details.location.state}
                  {landingBusiness.details.location.state &&
                    landingBusiness.details.location.city &&
                    ' - '}
                  {landingBusiness.details.location.city}
                </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'absolute',
                    left: '50%',
                    top: '15%',
                    transform: 'translate(-50%, -50%)',
                    borderRadius: '50%',
                    backgroundColor: `${client.primaryColor}`,
                    padding: '25px 22px',
                    width: '20px',
                    height: '20px',
                  }}
                >
                  <i
                    className="material-icons-outlined"
                    style={{
                      fontSize: '40px', // Tamanho do ícone
                      fontWeight: '100', // Diminui a espessura da linha
                    }}
                  >
                    location_on
                  </i>
                </div>
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    maxWidth: '42%',
                    justifyContent: 'flex-end',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {landingBusiness.details.location.region}
                  {landingBusiness.details.location.region &&
                    landingBusiness.details.location.neighborhood &&
                    ' - '}
                  {landingBusiness.details.location.neighborhood}
                </span>
              </div>
              <div
                className="col-sm-12 col-lg-12"
                style={{
                  marginTop: '-10px',
                }}
              >
                <img
                  src={landingBusiness.medias?.images?.housingUnitImages[0]}
                  alt="Apartment"
                  style={{
                    width: '100%',
                    height: '400px',
                    objectFit: 'cover',
                    borderRadius: '16px',
                  }}
                />
              </div>
              <div className="responsive-layout row flex-lg-row flex-column-reverse">
                <div
                  className="col-sm-12 col-lg-8 p-3 border rounded-4"
                  style={{
                    padding: '24px',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderRadius: '8px',
                    marginTop: '24px',
                  }}
                >
                  <div className="d-block d-md-flex">
                    <div style={{ width: '85%' }}>
                      <h1
                        style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          color: '#666',
                        }}
                      >
                        {landingBusiness.details.name}
                      </h1>
                      <p
                        style={{
                          color: '#666',
                          marginTop: '16px',
                          textAlign: 'justify',
                        }}
                      >
                        {landingBusiness.details.description}
                      </p>
                    </div>
                  </div>
                  {/* Tabs */}
                  <div
                    style={{
                      display: window.innerWidth <= 768 ? 'grid' : 'flex',
                      gap: '16px',
                      marginTop: '24px',
                    }}
                  >
                    {['apartment', 'condo', 'neighborhood'].map((tab) => (
                      <button
                        key={tab}
                        style={{
                          padding: '8px 16px',
                          cursor: 'pointer',
                          background: 'none',
                          borderRadius:
                            window.innerWidth <= 768 ? '10px' : null,
                          border:
                            window.innerWidth <= 768
                              ? '1px solid #dee2e6'
                              : 'none',
                          ...(activeTab === tab
                            ? {
                                color: `${client.primaryColor}`,
                                border:
                                  window.innerWidth <= 768
                                    ? `2px solid ${client.primaryColor}`
                                    : 'none',
                                borderBottom: `2px solid ${client.primaryColor}`,
                              }
                            : {
                                color: '#666',
                                borderBottom:
                                  window.innerWidth <= 768
                                    ? '2px solid #dee2e6'
                                    : 'none',
                              }),
                        }}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab === 'apartment'
                          ? 'O apartamento'
                          : tab === 'condo'
                            ? 'O condomínio'
                            : 'A vizinhança'}
                      </button>
                    ))}
                  </div>
                  <div
                    style={{
                      gap: '10px',
                      width: '100%',
                      justifyContent: 'space-between',
                      marginTop: '24px',
                      borderTop: '1px solid #eee',
                      borderBottom: '1px solid #eee',
                      padding: '16px 0',
                    }}
                  >
                    {activeTab === 'apartment' && (
                      <div style={{ display: 'block' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '20px',
                          }}
                        >
                          {!!landingBusiness.details.squareMeters && (
                            <FeatureIcon
                              icon={'design_services'}
                              text={
                                Array.isArray(
                                  landingBusiness.details.squareMeters
                                )
                                  ? `${landingBusiness.details.squareMeters[0]} - ${landingBusiness.details.squareMeters[1]} m²`
                                  : `${landingBusiness.details.squareMeters} m²`
                              }
                            />
                          )}
                          {!!landingBusiness.details.suites && (
                            <FeatureIcon
                              icon={'door_sliding'}
                              text={`${landingBusiness.details.suites} suítes`}
                            />
                          )}
                          {!!landingBusiness.details.bedrooms && (
                            <FeatureIcon
                              icon={'meeting_room'}
                              text={`${landingBusiness.details.bedrooms} quartos`}
                            />
                          )}
                          {!!landingBusiness.details.bathrooms && (
                            <FeatureIcon
                              icon={'wc'}
                              text={`${landingBusiness.details.bathrooms} banheiros`}
                            />
                          )}
                          {landingBusiness.details.garage && (
                            <FeatureIcon
                              icon={'directions_car'}
                              text={`${landingBusiness.details.garage} vagas`}
                            />
                          )}
                        </div>
                        <div>
                          {landingBusiness.details.location.regionBenefits && (
                            <div
                              style={{
                                display: 'block',
                                textAlign: 'justify',
                                marginTop: '10px',
                              }}
                            >
                              <p>
                                {
                                  landingBusiness.details.location
                                    .regionBenefits
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {activeTab === 'condo' && (
                      <div style={{ display: 'block' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            flexWrap: 'wrap',
                            gap: '20px',
                          }}
                        >
                          {landingBusiness.details.closedCondominium && (
                            <FeatureIcon
                              icon={'account_balance'}
                              text={'Condominio fechado'}
                            />
                          )}
                          {Object.entries(
                            landingBusiness.details.condominium
                          ).map(([key]) => (
                            <>
                              {key === 'gym' && (
                                <FeatureIcon
                                  icon={'directions_car'}
                                  text={'Academia'}
                                />
                              )}
                              {key === 'petArea' && (
                                <FeatureIcon icon={'pets'} text={'Area pet'} />
                              )}
                              {key === 'pool' && (
                                <FeatureIcon icon={'pool'} text={'Piscina'} />
                              )}
                              {key === 'barbecueArea' && (
                                <FeatureIcon
                                  icon={'outdoor_grill'}
                                  text={'Churrasqueira'}
                                />
                              )}
                              {key === 'sportsCourt' && (
                                <FeatureIcon
                                  icon={'sports_basketball'}
                                  text={'Quadra'}
                                />
                              )}
                              {key === 'coworkingSpace' && (
                                <FeatureIcon
                                  icon={'workspaces'}
                                  text={'Coworking'}
                                />
                              )}
                              {key === 'bikeRack' && (
                                <FeatureIcon
                                  icon={'pedal_bike'}
                                  text={'Bicicletário'}
                                />
                              )}
                              {key === 'beautySalon' && (
                                <FeatureIcon
                                  icon={'workspaces'}
                                  text={'Salão de beleza'}
                                />
                              )}
                              {key === 'alwaysAvailableReception' && (
                                <FeatureIcon
                                  icon={'lock'}
                                  text={'Portaria 24h'}
                                />
                              )}
                              {key === 'multifunctionalSpace' && (
                                <FeatureIcon
                                  icon={'fireplace'}
                                  text={'Espaço multiuso'}
                                />
                              )}
                              {key === 'laundry' && (
                                <FeatureIcon
                                  icon={'local_laundry_service'}
                                  text={'Lavanderia'}
                                />
                              )}
                              {key === 'sauna' && (
                                <FeatureIcon icon={'hot_tub'} text={'Sauna'} />
                              )}
                              {key === 'kidsPlayground' && (
                                <FeatureIcon
                                  icon={'child_friendly'}
                                  text={'Playground'}
                                />
                              )}
                              {key === 'miniMarket' && (
                                <FeatureIcon
                                  icon={'local_convenience_store'}
                                  text={'Minimercado'}
                                />
                              )}
                              {key === 'coveredGarage' && (
                                <FeatureIcon
                                  icon={'garage'}
                                  text={'Garagem coberta'}
                                />
                              )}
                              {key === 'partyRoom' && (
                                <FeatureIcon
                                  icon={'celebration'}
                                  text={'Salão de festa'}
                                />
                              )}
                            </>
                          ))}
                        </div>
                        {landingBusiness.details.condominium.others && (
                          <div
                            style={{
                              display: 'block',
                              textAlign: 'justify',
                              marginTop: '10px',
                            }}
                          >
                            <p>{landingBusiness.details.condominium.others}</p>
                          </div>
                        )}
                      </div>
                    )}
                    {activeTab === 'neighborhood' && (
                      <div style={{ display: 'block' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            flexWrap: 'wrap',
                            gap: '20px',
                          }}
                        >
                          {Object.entries(
                            landingBusiness.details.neighborhood
                          ).map(([key]) => (
                            <>
                              {key === 'fruitMarkets' && (
                                <FeatureIcon
                                  icon={'storefront'}
                                  text={'Sacolão'}
                                />
                              )}
                              {key === 'gyms' && (
                                <FeatureIcon
                                  icon={'fitness_center'}
                                  text={'Academias'}
                                />
                              )}
                              {key === 'supermarkets' && (
                                <FeatureIcon
                                  icon={'storefront'}
                                  text={'Supermercados'}
                                />
                              )}
                              {key === 'subway' && (
                                <FeatureIcon icon={'train'} text={'Metrô'} />
                              )}
                              {key === 'drugstores' && (
                                <FeatureIcon
                                  icon={'medication'}
                                  text={'Farmácia'}
                                />
                              )}
                              {key === 'daycares' && (
                                <FeatureIcon
                                  icon={'child_care'}
                                  text={'Creche'}
                                />
                              )}
                              {key === 'petshops' && (
                                <FeatureIcon icon={'pets'} text={'Petshop'} />
                              )}
                              {key === 'easyAccess' && (
                                <FeatureIcon
                                  icon={'accessible'}
                                  text={'Fácil acesso'}
                                />
                              )}
                              {key === 'policeStations' && (
                                <FeatureIcon
                                  icon={'local_police'}
                                  text={'Delegacia'}
                                />
                              )}
                              {key === 'hospitals' && (
                                <FeatureIcon
                                  icon={'monitor_heart'}
                                  text={'Hospitais'}
                                />
                              )}
                              {key === 'busStops' && (
                                <FeatureIcon
                                  icon={'directions_bus'}
                                  text={'Ponto de ônibus'}
                                />
                              )}
                              {key === 'schools' && (
                                <FeatureIcon icon={'school'} text={'Escolas'} />
                              )}
                            </>
                          ))}
                        </div>
                        {landingBusiness.details.neighborhood.others && (
                          <div
                            style={{
                              display: 'block',
                              textAlign: 'justify',
                              marginTop: '10px',
                            }}
                          >
                            <p>{landingBusiness.details.neighborhood.others}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {images?.length > 0 && (
                    <div>
                      <a
                        style={{
                          display: 'grid',
                          gap: '5px',
                          width: '100%',
                          height: '500px',
                          margin: '0 auto',
                          borderRadius: '10px',
                          overflow: 'hidden',
                          background: 'none',
                          cursor: 'pointer',
                        }}
                        onClick={() => setImageModal(true)}
                      >
                        <div
                          style={{
                            backgroundColor: '#ccc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            gridColumn: '1 / 2',
                            gridRow: '1 / 3',
                          }}
                        >
                          <img
                            src={images[0]}
                            alt={`Imagem 1`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                        {images.length > 1 && (
                          <div
                            style={{
                              backgroundColor: '#ccc',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              gridColumn: '2 / 3',
                              gridRow: '1 / 2',
                            }}
                          >
                            <img
                              src={images[1]}
                              alt={`Imagem 2`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </div>
                        )}
                        {images.length > 2 && (
                          <div
                            style={{
                              backgroundColor: '#ccc',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              gridColumn: '2 / 3',
                              gridRow: '2 / 2',
                            }}
                          >
                            <img
                              src={images[2]}
                              alt={`Imagem 3`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </div>
                        )}
                      </a>
                      <div
                        className="row"
                        style={{
                          display: 'flex',
                          marginTop: '19px',
                        }}
                      >
                        <div
                          className="col-12 col-sm-8"
                          style={{
                            display: 'flex',
                            gap: '15px',
                            marginBottom: '10px',
                            fontSize: '17px',
                          }}
                        >
                          {landingBusiness.medias.virtualTourUrl && (
                            <button
                              style={{
                                display: 'flex',
                                flexDirection:
                                  window.innerWidth <= 768
                                    ? 'column'
                                    : undefined,
                                alignItems: 'center',
                                padding: '10px',
                                borderRadius: '10px',
                                border: '1px solid #666666',
                                backgroundColor: 'white',
                              }}
                              onClick={() => {
                                window.open(
                                  `${landingBusiness.medias.virtualTourUrl}`,
                                  'blank'
                                );
                              }}
                            >
                              <i className="material-icons-outlined">
                                view_in_ar
                              </i>
                              3D Tour
                            </button>
                          )}
                          {landingBusiness.medias.videoUrl && (
                            <button
                              style={{
                                display: 'flex',
                                flexDirection:
                                  window.innerWidth <= 768
                                    ? 'column'
                                    : undefined,
                                alignItems: 'center',
                                padding: '10px',
                                borderRadius: '10px',
                                border: '1px solid #666666',
                                backgroundColor: 'white',
                              }}
                              onClick={() =>
                                window.open(
                                  `${landingBusiness.medias.videoUrl}`,
                                  'blank'
                                )
                              }
                            >
                              <i className="material-icons-outlined">
                                subscriptions
                              </i>
                              Videos
                            </button>
                          )}
                          {landingBusiness.medias.images.blueprintImages && (
                            <button
                              style={{
                                display: 'flex',
                                flexDirection:
                                  window.innerWidth <= 768
                                    ? 'column'
                                    : undefined,
                                alignItems: 'center',
                                padding: '10px',
                                borderRadius: '10px',
                                border: '1px solid #666666',
                                backgroundColor: blueprintModal
                                  ? '#c04000'
                                  : 'white',
                                color: blueprintModal ? 'white' : 'black',
                              }}
                              onClick={() => {
                                setBlueprintModal(!blueprintModal);
                                setImageModal(true);
                              }}
                            >
                              <i className="material-icons-outlined">
                                square_foot
                              </i>
                              Planta
                            </button>
                          )}
                        </div>
                        <div
                          className="responsive-layout col-12 col-sm-4 d-flex justify-content-evenly justify-content-sm-end"
                          style={{
                            display: 'flex',
                            gap: '15px',
                            marginBottom: '10px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                            }}
                          >
                            <button
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px',
                                borderRadius: '100%',
                                border: '1px solid #666666',
                                backgroundColor: like ? '#c04000' : 'white',
                                color: like ? 'white' : 'black',
                              }}
                              onClick={() => {
                                if (like === false) {
                                  sendLike();
                                }
                                setLike(true);
                              }}
                            >
                              <i className="material-icons-outlined">
                                favorite
                              </i>
                            </button>
                            {landingBusiness.stats?.likes
                              ? landingBusiness.stats?.likes
                              : '0'}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                            }}
                          >
                            <button
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px',
                                borderRadius: '100%',
                                border: '1px solid #666666',
                                backgroundColor: shareModal
                                  ? '#c04000'
                                  : 'white',
                                color: shareModal ? 'white' : 'black',
                              }}
                              onClick={() => {
                                sendShare();
                                setShareModal(true);
                                setShare(true);
                              }}
                            >
                              <i className="material-icons-outlined">share</i>
                            </button>
                            {landingBusiness.stats?.shares
                              ? landingBusiness.stats?.shares
                              : '0'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <hr
                    style={{
                      border: '1px solid #ccc',
                      width: '100%',
                      margin: '20px 0',
                    }}
                  />
                  <h1
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#666',
                      marginTop: '20px',
                    }}
                  >
                    Diferenciais
                  </h1>
                  <p
                    style={{
                      color: '#666',
                      marginTop: '16px',
                      textAlign: 'justify',
                    }}
                  >
                    {landingBusiness.details?.location?.regionBenefits}
                  </p>
                </div>

                <div
                  className="col-sm-12 col-lg-4"
                  style={{ paddingLeft: '10px' }}
                >
                  {/* Price Info */}
                  <div
                    style={{
                      position: 'relative',
                      marginBottom: window.innerWidth <= 768 ? '0' : '24px',
                      marginTop: window.innerWidth <= 768 ? '-15px' : '-30px',
                      borderRadius: '10px',
                      border:
                        window.innerWidth <= 768 ? null : '1px solid #dee2e6',
                      zIndex: '0',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: `${client.primaryColor}`,
                        color: 'white',
                        padding: '8px 24px',
                        borderRadius:
                          window.innerWidth <= 768 ? '8px' : '8px 8px 0px 0px',
                        fontSize: window.innerWidth <= 768 ? '17px' : '25px',
                        fontWeight: '500',
                        textAlign: 'center',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        width: window.innerWidth <= 768 ? '90%' : '100%',
                      }}
                    >
                      {landingBusiness.details.constructionStage
                        ?.advancedConstruction && 'Obras Avançadas'}
                      {landingBusiness.details.constructionStage?.blueprint &&
                        'Na Planta'}
                      {landingBusiness.details.constructionStage?.launched &&
                        'Lançamento'}
                      {landingBusiness.details.constructionStage
                        ?.readyToMoveIn && 'Pronto para morar'}
                      {landingBusiness.details.constructionStage
                        ?.underConstruction && 'Em obras'}
                      {landingBusiness.details.constructionStage
                        ?.upcomingLaunch && 'Breve lançamento'}
                    </div>
                    {landingBusiness.details?.type !== 'Alto Padrão' && (
                      <div className="p-3">
                        <div
                          style={{
                            display: 'flex',
                            gap: '10px',
                            marginBottom: '20px',
                            justifyContent: 'space-around',
                          }}
                        >
                          {!!landingBusiness.customerPaymentConditions
                            ?.householdIncome && (
                            <div
                              style={{
                                backgroundColor: `${client.primaryColor}`,
                                color: 'white',
                                padding: '8px 15px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                textAlign: 'center',
                                alignContent: 'center',
                              }}
                            >
                              Renda de até:
                              <h2
                                style={{
                                  fontSize: '20px',
                                  fontWeight: '800',
                                  margin: '0',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '15px',
                                    fontWeight: '500',
                                  }}
                                >
                                  R$
                                </span>{' '}
                                {maskMoney(
                                  landingBusiness.customerPaymentConditions
                                    ?.householdIncome
                                )}
                              </h2>
                              por mês
                            </div>
                          )}
                          {!!landingBusiness.customerPaymentConditions
                            ?.maximumSubsidy && (
                            <div
                              style={{
                                backgroundColor: `${client.primaryColor}`,
                                color: 'white',
                                padding: '8px 15px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                textAlign: 'center',
                                alignContent: 'center',
                              }}
                            >
                              Subsídio de até:
                              <h2
                                style={{
                                  fontSize: '20px',
                                  fontWeight: '800',
                                  margin: '0',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '15px',
                                    fontWeight: '500',
                                  }}
                                >
                                  R$
                                </span>
                                {maskMoney(
                                  landingBusiness.customerPaymentConditions
                                    ?.maximumSubsidy
                                )}
                              </h2>
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            gap: '10px',
                          }}
                        >
                          {landingBusiness.details.paymentConditions
                            ?.minhaCasaMinhaVida && (
                            <img
                              style={{
                                width:
                                  window.innerWidth <= 768 ? '60px' : '70px',
                              }}
                              src={minhaCasaMinhaVida}
                              alt="Minha Casa Minha Vida"
                            />
                          )}
                          {landingBusiness.details.paymentConditions
                            ?.casaVerdeAmarela && (
                            <img
                              style={{
                                width:
                                  window.innerWidth <= 768 ? '60px' : '70px',
                              }}
                              src={casaVerdeEAmarela}
                              alt="Casa Verde e Amarela"
                            />
                          )}
                          {landingBusiness.details.paymentConditions?.fgts && (
                            <img
                              style={{
                                width:
                                  window.innerWidth <= 768 ? '60px' : '70px',
                              }}
                              src={FGTS}
                              alt="FGTS"
                            />
                          )}
                          {landingBusiness.details.paymentConditions
                            ?.podeEntrar && (
                            <img
                              style={{
                                width:
                                  window.innerWidth <= 768 ? '60px' : '70px',
                              }}
                              src={podeEntrar}
                              alt="Pode Entrar"
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {landingBusiness.details?.type === 'Alto Padrão' && (
                      <div className="p-3">
                        <div
                          className="d-flex flex-column flex-lg-row"
                          style={{
                            display: 'flex',
                            gap: '10px',
                            marginBottom: '20px',
                            justifyContent: 'space-around',
                          }}
                        >
                          {!!landingBusiness.customerPaymentConditions
                            ?.UnitsMinimumValue && (
                            <div
                              style={{
                                backgroundColor: `${client.primaryColor}`,
                                color: 'white',
                                padding: '8px 15px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                textAlign: 'center',
                                alignContent: 'center',
                              }}
                            >
                              Unidades a partir de:
                              <h2
                                style={{
                                  fontSize: '20px',
                                  fontWeight: '800',
                                  margin: '0',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '15px',
                                    fontWeight: '500',
                                  }}
                                >
                                  R$
                                </span>{' '}
                                {maskMoneyWithText(
                                  landingBusiness.customerPaymentConditions
                                    ?.UnitsMinimumValue
                                )}{' '}
                                <span
                                  style={{
                                    fontSize: '15px',
                                    fontWeight: '500',
                                  }}
                                >
                                  até R${' '}
                                </span>
                                {maskMoneyWithText(
                                  landingBusiness.customerPaymentConditions
                                    ?.UnitsMaximumValue
                                )}
                              </h2>
                            </div>
                          )}
                          {!!landingBusiness.customerPaymentConditions
                            ?.InstallmentsMinimumValue && (
                            <div
                              style={{
                                backgroundColor: `${client.primaryColor}`,
                                color: 'white',
                                padding: '8px 15px',
                                borderRadius: '8px',
                                fontSize: '13px',
                                textAlign: 'center',
                                alignContent: 'center',
                              }}
                            >
                              Parcelas mensais a partir de:
                              <h2
                                style={{
                                  fontSize: '20px',
                                  fontWeight: '800',
                                  margin: '0',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '15px',
                                    fontWeight: '500',
                                  }}
                                >
                                  R${' '}
                                </span>
                                {maskMoney(
                                  landingBusiness.customerPaymentConditions
                                    ?.InstallmentsMinimumValue
                                )}
                              </h2>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-3 border rounded-4">
                    {/* Contact Form */}
                    <div
                      style={{
                        borderRadius: '8px',
                      }}
                    >
                      {!!client.logoURL && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '10px',
                          }}
                        >
                          <img
                            src={client.logoURL}
                            alt="Logo do Programa"
                            style={{ height: '48px' }}
                          />
                        </div>
                      )}
                      <h3
                        style={{
                          fontSize: '20px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          color: `${client.primaryColor}`,
                        }}
                      >
                        Solicitar proposta
                      </h3>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '16px',
                        }}
                      >
                        <input
                          type="text"
                          placeholder="Nome"
                          style={{
                            padding: '8px',
                            borderRadius: '4px',
                            backgroundColor: '#00000010',
                            border: 'none',
                            width: '100%',
                            marginTop: '16px',
                          }}
                          value={leadName}
                          onChange={(event) => {
                            setLeadName(event.target.value);
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Sobrenome"
                          style={{
                            padding: '8px',
                            borderRadius: '4px',
                            backgroundColor: '#00000010',
                            border: 'none',
                            width: '100%',
                            marginTop: '16px',
                          }}
                          value={leadLastName}
                          onChange={(event) => {
                            setLeadLastName(event.target.value);
                          }}
                        />
                      </div>
                      <input
                        type="email"
                        placeholder="Email"
                        style={{
                          padding: '8px',
                          borderRadius: '4px',
                          backgroundColor: '#00000010',
                          border: 'none',
                          width: '100%',
                          marginTop: '16px',
                        }}
                        value={leadEmail}
                        onChange={(event) => {
                          setLeadEmail(event.target.value);
                        }}
                      />
                      <input
                        type="tel"
                        placeholder="Telefone/WhatsApp com DDD"
                        style={{
                          padding: '8px',
                          borderRadius: '4px',
                          backgroundColor: '#00000010',
                          border: 'none',
                          width: '100%',
                          marginTop: '16px',
                        }}
                        value={leadPhone}
                        onChange={(event) => {
                          setLeadPhone(maskTEL(event.target.value));
                        }}
                      />
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: '16px',
                          fontSize: '14px',
                          color: '#666',
                        }}
                      >
                        <div
                          style={{
                            marginRight: '10px',
                            width: '20px',
                            height: '20px',
                            marginBottom: 'auto',
                          }}
                        >
                          <input
                            className={`form-check-input`}
                            style={{
                              border: `2px solid ${client.primaryColor}`,
                            }}
                            type="checkbox"
                            value=""
                            id="flexCheckDefault"
                            onClick={() => {
                              setAcceptedTerms(!acceptedTerms);
                            }}
                            checked={acceptedTerms}
                          />
                        </div>
                        <p style={{ textAlign: 'justify' }}>
                          De acordo com as Leis 12.965/2014 e 13.709/2018, que
                          regulam o uso da Internet e o tratamento de dados
                          pessoais no Brasil, ao autorizo{' '}
                          {client?.companyName
                            ? client?.companyName
                            : 'OQ Digital'}{' '}
                          a enviar notificações por e-mail ou outros meios e
                          concordo com sua{' '}
                          <a
                            target="_blank"
                            href="https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPoli%CC%81tica%20de%20Privacidade_OQ%20MIDIA%20DIGITAL%20JULHO%202020.docx.pdf?alt=media&token=73c99224-963a-40c8-b385-618bb2cc5122"
                          >
                            Política de Privacidade
                          </a>{' '}
                          e{' '}
                          <a
                            onClick={() =>
                              navigate(`${routes.TERMS}`, {
                                state: {
                                  isCameFromActionPage: true,
                                  clientId: client?.id,
                                },
                              })
                            }
                            style={{ color: 'blue', cursor: 'pointer' }}
                          >
                            Termos de uso
                          </a>
                          .
                        </p>
                      </div>
                      <button
                        style={{
                          width: '100%',
                          backgroundColor: `${client.primaryColor}`,
                          color: 'white',
                          padding: '12px',
                          borderRadius: '4px',
                          border: 'none',
                          marginTop: '16px',
                          cursor: 'pointer',
                        }}
                        disabled={
                          !acceptedTerms ||
                          !leadName ||
                          !leadLastName ||
                          !leadPhone ||
                          !leadEmail
                        }
                        onClick={() => {
                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                          if (!emailRegex.test(leadEmail)) {
                            Notify(
                              'Por favor, insira um email válido.',
                              'error'
                            );
                          } else {
                            saveLead({
                              leadName,
                              leadLastName,
                              leadPhone,
                              leadEmail,
                            });
                          }
                        }}
                      >
                        SOLICITAR PROPOSTA
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '10px',
                      fontSize: '10px',
                    }}
                  >
                    <p
                      style={{
                        color: 'green',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <i className="material-icons-outlined fs-6">lock</i> Este
                      é um ambiente seguro!
                    </p>
                    <p>
                      Trabalhamos constantemente para proteger sua segurança e
                      privacidade.{' '}
                      <a
                        target="_blank"
                        href="https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPoli%CC%81tica%20de%20Privacidade_OQ%20MIDIA%20DIGITAL%20JULHO%202020.docx.pdf?alt=media&token=73c99224-963a-40c8-b385-618bb2cc5122"
                      >
                        Saiba mais
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Location */}
              {!!landingBusiness.details.location.googleMapsLink && (
                <div
                  style={{
                    marginTop: '32px',
                  }}
                >
                  <h3
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                    }}
                  >
                    Localização
                  </h3>
                  <iframe
                    src={`https://www.google.com/maps/embed?pb=${landingBusiness.details.location.googleMapsLink}`}
                    width="100%"
                    height="450"
                    style={{ border: 0 }}
                    allowFullScreen={true}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              )}

              <div className="row" style={{ marginTop: '32px' }}>
                <div className="col-sm-12 col-lg-6">
                  <h3
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                    }}
                  >
                    Nas proximidades do imóvel
                  </h3>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '20px',
                      fontWeight: '',
                      marginBottom: '10px',
                    }}
                  >
                    {Object.entries(landingBusiness.details.neighborhood).map(
                      ([key]) => (
                        <>
                          {key === 'fruitMarkets' && (
                            <FeatureIcon icon={'storefront'} text={'Sacolão'} />
                          )}
                          {key === 'gyms' && (
                            <FeatureIcon
                              icon={'fitness_center'}
                              text={'Academias'}
                            />
                          )}
                          {key === 'supermarkets' && (
                            <FeatureIcon
                              icon={'storefront'}
                              text={'Supermercados'}
                            />
                          )}
                          {key === 'subway' && (
                            <FeatureIcon icon={'train'} text={'Metrô'} />
                          )}
                          {key === 'drugstores' && (
                            <FeatureIcon
                              icon={'medication'}
                              text={'Farmácia'}
                            />
                          )}
                          {key === 'daycares' && (
                            <FeatureIcon icon={'child_care'} text={'Creche'} />
                          )}
                          {key === 'petshops' && (
                            <FeatureIcon icon={'pets'} text={'Petshop'} />
                          )}
                          {key === 'easyAccess' && (
                            <FeatureIcon
                              icon={'accessible'}
                              text={'Fácil acesso'}
                            />
                          )}
                          {key === 'policeStations' && (
                            <FeatureIcon
                              icon={'local_police'}
                              text={'Delegacia'}
                            />
                          )}
                          {key === 'hospitals' && (
                            <FeatureIcon
                              icon={'monitor_heart'}
                              text={'Hospitais'}
                            />
                          )}
                          {key === 'busStops' && (
                            <FeatureIcon
                              icon={'directions_bus'}
                              text={'Ponto de ônibus'}
                            />
                          )}
                          {key === 'schools' && (
                            <FeatureIcon icon={'school'} text={'Escolas'} />
                          )}
                        </>
                      )
                    )}
                  </div>
                </div>
                {(landingBusiness.medias.videoUrl ||
                  landingBusiness.medias.virtualTourUrl) && (
                  <div className="col-sm-12 col-lg-6">
                    <div
                      style={{
                        borderRadius: '10px',
                        border:
                          window.innerWidth <= 768 ? null : '1px solid #dee2e6',
                        position: 'relative',
                        marginBottom: '24px',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: `${client.primaryColor}`,
                          color: 'white',
                          padding: '8px 24px',
                          marginBottom: '5px',
                          borderRadius:
                            window.innerWidth <= 768
                              ? '8px'
                              : '8px 8px 0px 0px',
                          fontSize: window.innerWidth <= 768 ? '17px' : '25px',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Mais sobre o empreendimento
                      </div>
                      <div
                        className="row"
                        style={{
                          margin: window.innerWidth <= 768 ? null : '10px',
                        }}
                      >
                        {landingBusiness.medias.images.blueprintImages && (
                          <div
                            className="col-sm-12 col-lg-6"
                            style={{ marginBottom: '5px' }}
                          >
                            <div
                              className="border rounded-4"
                              style={{ padding: '10px' }}
                            >
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingBottom: '5px',
                                  gap: '5px',
                                }}
                              >
                                <i className="material-icons-outlined">
                                  square_foot
                                </i>
                                Planta
                              </span>
                              <img
                                src={
                                  landingBusiness.medias.images
                                    .blueprintImages[0]
                                }
                                style={{
                                  width: '100%',
                                  cursor: 'pointer',
                                  borderRadius: '8px',
                                }}
                                onClick={() => {
                                  setBlueprintModal(!blueprintModal);
                                  setImageModal(true);
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {landingBusiness.medias.virtualTourUrl && (
                          <div
                            className="col-sm-12 col-lg-6"
                            style={{ marginBottom: '5px' }}
                          >
                            <div
                              className="border rounded-4"
                              style={{ padding: '10px' }}
                            >
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingBottom: '5px',
                                  gap: '5px',
                                }}
                              >
                                <i className="material-icons-outlined">
                                  view_in_ar
                                </i>
                                3D Tour
                              </span>
                              {landingBusiness.medias?.virtualTourUrl?.includes(
                                'youtube'
                              ) ? (
                                <a
                                  className="border rounded-4"
                                  href={landingBusiness.medias.virtualTourUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    src={`https://img.youtube.com/vi/${landingBusiness.medias?.virtualTourUrl?.match(/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)[1]}/hqdefault.jpg`}
                                    style={{
                                      width: '100%',
                                      cursor: 'pointer',
                                      borderRadius: '8px',
                                    }}
                                  />
                                </a>
                              ) : (
                                <div
                                  style={{
                                    position: 'relative',
                                    width: '100%',
                                    borderRadius: '8px',
                                  }}
                                >
                                  <iframe
                                    src={landingBusiness.medias.virtualTourUrl}
                                    style={{
                                      width: '100%',
                                      height: '180px',
                                      borderRadius: '8px',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                  <div
                                    onClick={() => setIFrameModal(true)}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      cursor: 'pointer',
                                      opacity: 0,
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Recommendations */}
              <div
                style={{
                  marginTop: '32px',
                }}
              >
                <h3
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                  }}
                >
                  Conheça outras oportunidades para você!
                </h3>
                <div
                  className="row"
                  style={{
                    display: 'flex',
                    gap: '16px',
                    marginTop: '16px',
                  }}
                >
                  {clientLandingBusiness?.map((Lb, index) => (
                    <div
                      key={index}
                      className="col-lg-3 pt-3"
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={Lb.thumbnail}
                        alt={Lb.name}
                        style={{
                          width: '100%',
                          height: '192px',
                          objectFit: 'cover',
                        }}
                      />
                      <div
                        style={{
                          padding: '16px',
                        }}
                      >
                        <h4
                          style={{
                            fontWeight: 'bold',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                        >
                          {Lb.name}
                        </h4>
                        <p
                          style={{
                            color: '#666',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            overflow: 'auto',
                          }}
                        >
                          {Lb.city}
                        </p>
                        <button
                          style={{
                            width: '100%',
                            backgroundColor: `${client.primaryColor}`,
                            color: 'white',
                            padding: '12px',
                            borderRadius: '4px',
                            border: 'none',
                            marginTop: '16px',
                            cursor: 'pointer',
                            fontSize: '20px',
                          }}
                          onClick={() => {
                            window.open(
                              routes.LANDING_BUSSINESS.replace(
                                ':playerId',
                                player.id
                              ).replace(':bussinessId', Lb.id)
                            );
                          }}
                        >
                          Ver mais
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Footer */}
              <footer
                style={{
                  marginTop: '32px',
                  padding: '16px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#666',
                  borderTop: '1px solid #eee',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection:
                      window.innerWidth <= 768 ? 'column' : undefined,
                    justifyContent: 'center',
                    gap: '16px',
                    marginBottom: '16px',
                  }}
                >
                  <a
                    href="https://firebasestorage.googleapis.com/v0/b/oqhomapi.appspot.com/o/TermsAndPolitics%2FPoli%CC%81tica%20de%20Privacidade_OQ%20MIDIA%20DIGITAL%20JULHO%202020.docx.pdf?alt=media&token=73c99224-963a-40c8-b385-618bb2cc5122"
                    style={{
                      color: '#666',
                      textDecoration: 'none',
                    }}
                    target="_blank"
                  >
                    Política de privacidade
                  </a>
                  <a
                    onClick={() =>
                      navigate(`${routes.TERMS}`, {
                        state: {
                          isCameFromActionPage: true,
                          clientId: client?.id,
                        },
                      })
                    }
                    style={{
                      color: '#666',
                      textDecoration: 'none',
                    }}
                  >
                    Termos de uso
                  </a>
                  <a
                    href="#"
                    style={{
                      color: '#666',
                      textDecoration: 'none',
                    }}
                  >
                    Empreendimentos
                  </a>
                </div>
                <a
                  href="https://oqindicar.com/"
                  target="_blank"
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  © 2025 OQ Digital, Todos os direitos reservados
                </a>
              </footer>
            </>
          )}
        </div>
      )}
      <WhatsAppCapture
        data={(e: any) => {
          saveLead({
            name: e.name,
            lastname: '',
            phone: e.phone,
            email: e.email,
          });
        }}
        isOpen={isOpenWhatsAppCapture}
        client={client}
        player={player}
        host={{}}
      />
      <IFrameModal
        url={landingBusiness?.medias?.virtualTourUrl}
        modalIsOpen={iFrameModal}
        toggleModal={() => {
          setIFrameModal(false);
        }}
      />
      <ImageModal
        images={
          blueprintModal
            ? landingBusiness.medias.images.blueprintImages
            : images
        }
        modalIsOpen={imageModal}
        toggleModal={() => {
          setImageModal(false);
          setBlueprintModal(false);
        }}
      />
      <ShareModal
        landingBusiness={landingBusiness}
        playerId={playerId}
        client={client}
        modalIsOpen={shareModal}
        toggleModal={() => {
          setShareModal(false);
        }}
      />
    </div>
  );
}
